import type { Category } from '../types'
import { useFirestore } from './firebase'
import * as firestoreCategories from './firestore/categories'
import { sortCategoriesByIndex } from './menuSortOrder'

const STORAGE_KEY = 'wortelogy-categories'

export const UNCATEGORIZED_CATEGORY_ID = '__uncategorized__'
export const UNCATEGORIZED_NAME = 'Uncategorized'

const uncategorizedCategory: Category = {
  id: UNCATEGORIZED_CATEGORY_ID,
  name: UNCATEGORIZED_NAME,
  sortIndex: 0,
}

function readCategoriesLocalUnsorted(): Category[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    let list: Category[] = raw ? (JSON.parse(raw) as Category[]) : []
    const hasUncategorized = list.some((c) => c.id === UNCATEGORIZED_CATEGORY_ID)
    if (!hasUncategorized) {
      list = [uncategorizedCategory, ...list]
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
    }
    return list
  } catch {
    return [uncategorizedCategory]
  }
}

function getCategoriesLocal(): Category[] {
  return sortCategoriesByIndex(readCategoriesLocalUnsorted())
}

function saveCategoriesLocal(categories: Category[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(categories))
}

function generateCategoryId(): string {
  return `C-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
}

export function getCategories(): Category[] {
  if (useFirestore()) {
    // Only use place-scoped Firestore cache; never fall back to global localStorage
    // to avoid leaking another account/place's categories.
    return firestoreCategories.getCategories()
  }
  return getCategoriesLocal()
}

export function saveCategories(categories: Category[]): void {
  if (useFirestore()) {
    firestoreCategories.saveCategories(categories)
    return
  }
  const withIdx = categories.map((c, i) => ({ ...c, sortIndex: i }))
  saveCategoriesLocal(withIdx)
}

export function addCategory(category: Omit<Category, 'id'>): Category {
  if (useFirestore()) return firestoreCategories.addCategory(category)
  const id = generateCategoryId()
  const list = readCategoriesLocalUnsorted()
  const maxIdx = list.reduce((m, c) => Math.max(m, c.sortIndex ?? -1), -1)
  const full: Category = { ...category, id, sortIndex: maxIdx + 1 }
  saveCategories([...list, full])
  return full
}

export function updateCategory(id: string, patch: Partial<Category>): Category[] {
  if (useFirestore()) return firestoreCategories.updateCategory(id, patch)
  const list = readCategoriesLocalUnsorted()
  const updated = list.map((c) => (c.id === id ? { ...c, ...patch } : c))
  saveCategories(updated)
  return getCategoriesLocal()
}

export function deleteCategory(id: string): Category[] {
  if (useFirestore()) return firestoreCategories.deleteCategory(id)
  if (id === UNCATEGORIZED_CATEGORY_ID) return getCategoriesLocal()
  const filtered = readCategoriesLocalUnsorted().filter((c) => c.id !== id)
  saveCategories(filtered)
  return getCategoriesLocal()
}

export function reorderCategories(orderedIds: string[]): void {
  if (useFirestore()) {
    firestoreCategories.reorderCategories(orderedIds)
    return
  }
  const categories = getCategoriesLocal()
  const byId = new Map(categories.map((c) => [c.id, c]))
  const newOrder = orderedIds
    .map((id) => byId.get(id))
    .filter((c): c is Category => c != null)
  saveCategories(newOrder)
}
